import { useEffect } from 'react';
import {
    Box,
    Drawer as MuiDrawer,
    Grid,
    useMediaQuery,
    BoxProps,
    IconButton,
    Theme,
    CSSObject,
    Tooltip,
} from '@mui/material';
import MenuOpenRounded from '@mui/icons-material/MenuOpenRounded';
import MainNavMenu from './MainNavMenu';
import { useNavigation } from '@/providers/navigation-provider';
import { useQuery } from '@apollo/client';
import { MainNavDocument } from '@/graphql/generated/main-nav.generated';
import { useTheme, styled } from '@mui/material/styles';
import Cookies from 'js-cookie';
import { COOKIES } from '@/constants';
import { Link } from 'react-router-dom';
import { useSession } from '@/providers/session-provider';

export const drawerWidth = 256;
export const miniDrawerWidth = 57;
export const submenuWidth = 212;

const DrawerHeader = styled('div', { shouldForwardProp: (prop) => prop !== 'open' })<{ open?: boolean }>(
    ({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1),
    }),
);

const openedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    overflowX: 'hidden',
    width: miniDrawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen / 2,
    }),
});

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open' && prop !== 'openAbsolute',
})<{
    openAbsolute?: boolean;
}>(({ theme, open, openAbsolute }) => ({
    width: openAbsolute ? miniDrawerWidth : drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': {
            width: drawerWidth,
            padding: theme.spacing(1),
            ...openedMixin(theme),
        },
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
    '&.MuiDrawer-root': {
        zIndex: theme.zIndex.drawer + 1,
    },
}));

const SwonkieLogo = styled('img')<BoxProps>(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
    ...(theme.palette.mode === 'dark' && {
        filter: 'brightness(1.5)',
    }),
}));

const SwonkieLettering = styled('img')<BoxProps>(({ theme }) => ({
    marginLeft: '1rem',
    [theme.breakpoints.down('sm')]: {
        marginLeft: '2rem',
    },
    ...(theme.palette.mode === 'dark' && {
        filter: 'brightness(0) invert(1)',
    }),
}));

const SwonkieDevLettering = styled('div')(({ theme }) => ({
    marginLeft: '1rem',
    fontFamily: 'monospace',
    color: '#0F31F2',
    fontWeight: 700,
    marginTop: '-13px',
    fontSize: '1.1rem',
    [theme.breakpoints.down('sm')]: {
        marginLeft: '2rem',
    },
}));

const CloseMenuMobile = styled(IconButton)(({ theme }) => ({
    padding: 0,
    [theme.breakpoints.up('sm')]: {
        display: 'none',
    },
}));

export default function MainNav() {
    const { mainNavOpen, setMainNavOpen, mainNavOpenAbsolute } = useNavigation();
    const { workspace } = useSession();
    const cookieOpened = Cookies.get(COOKIES.MAIN_NAV_OPENED);

    const theme = useTheme();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

    const { data } = useQuery(MainNavDocument, { notifyOnNetworkStatusChange: true });

    useEffect(() => {
        setMainNavOpen(isLargeScreen ? false : cookieOpened !== undefined ? cookieOpened === '1' : false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLargeScreen]);

    const mainNav = () => (
        <>
            <DrawerHeader>
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems={'center'}
                    display={mainNavOpen ? 'flex' : 'none'}
                    minWidth={`calc(${drawerWidth}px - 2rem)`}
                >
                    <Grid display={'flex'} alignItems={'center'}>
                        <Link to="/home" style={{ display: 'contents' }}>
                            <SwonkieLogo src="/swonkie-logo.svg" alt="Swonkie Logo" width={40} />
                        </Link>
                        <CloseMenuMobile onClick={() => setMainNavOpen(false)} sx={{ p: 0 }}>
                            <MenuOpenRounded />
                        </CloseMenuMobile>
                        {workspace.isSwonkieDev ? (
                            <SwonkieDevLettering>swonkie.dev</SwonkieDevLettering>
                        ) : (
                            <SwonkieLettering src="/swonkie-lettering.svg" alt="Swonkie Logo" height={20} />
                        )}
                    </Grid>
                </Grid>

                <Box display={!mainNavOpen ? 'flex' : 'none'} flexGrow={1} justifyContent="center">
                    <Tooltip title="Swonkie" arrow placement="right">
                        <Link to="/home">
                            <SwonkieLogo src="/swonkie-logo.svg" alt="Swonkie Logo" width={40} />
                        </Link>
                    </Tooltip>
                </Box>
            </DrawerHeader>

            <MainNavMenu currentSubscription={data?.subscriptionPreview} />
        </>
    );

    const open = mainNavOpen || mainNavOpenAbsolute;

    if (isMobile) {
        return (
            <MuiDrawer
                open={open}
                onClose={() => setMainNavOpen(false)}
                sx={{ zIndex: theme.zIndex.drawer + 1 }}
            >
                <Box
                    role="presentation"
                    sx={{ p: theme.spacing(1), width: drawerWidth }}
                    flexGrow={1}
                    display="flex"
                    flexDirection="column"
                >
                    {mainNav()}
                </Box>
            </MuiDrawer>
        );
    }

    // Drawer when hover open & close (disable due to UX)
    // {...(!(mainNavOpen && !mainNavOpenAbsolute) && {
    //     onMouseOver: () => setMainNavOpen(true, true),
    //     onMouseLeave: () => setMainNavOpen(false),
    // })}

    return (
        <Drawer
            variant="permanent"
            open={open}
            onClose={() => setMainNavOpen(false)}
            openAbsolute={mainNavOpenAbsolute}
        >
            {mainNav()}
        </Drawer>
    );
}
